import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { resolveThumbnailUrl } from '../utils/hlsPlayer';
import { useAuth, usePlayer } from '../contexts/AppContext';
import { useAuthPrompt } from '../components/AuthPromptModal';
import { Play, Search, Heart, Share2, ListMusic, Volume2, VolumeX, ChevronRight, SlidersHorizontal, Flame, Sparkles, Music as MusicIcon, Film as FilmIcon } from 'lucide-react';
import { CommentModal } from '../components/CommentSection';
import { AddToPlaylistModal } from '../components/AddToPlaylistModal';
import { shareVideo } from '../utils/share';
import { AIDisclosureBadges } from '../components/AIDisclosure';
import { HeaderLockup } from '../components/HeaderLockup';
import { TrendingCard, LatestCard } from '../components/RailCards';
import { LiveBanner } from '../components/LiveBanner';

const API = process.env.REACT_APP_BACKEND_URL;

const GENRE_PILLS = ['All', 'Pop', 'Rock', 'Hip-Hop / Rap', 'R&B / Soul', 'Electronic', 'Jazz', 'Country', 'Latin', 'Indie', 'K-Pop', 'Afrobeats'];

const formatCount = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
};

const formatDuration = (seconds) => {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// ─── Hero Video Card ─────────────────────────────────────────
const HeroCard = ({ video, thumbnail, onClick, onArtistClick, muted, onToggleMute }) => {
  const navigate = useNavigate();
  return (
    <div className="relative rounded-2xl overflow-hidden cursor-pointer" onClick={onClick} data-testid="hero-card">
      <img
        src={thumbnail}
        alt={video.title}
        className="w-full h-[140px] sm:h-[180px] object-cover"
      />
      {/* Top badge */}
      <div className="absolute top-3 left-3 flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5 text-orange-400" />
        <span className="text-xs font-bold text-orange-400 tracking-wide">SPOTLIGHT</span>
      </div>
      <div className="absolute top-3 right-3">
        <button
          onClick={(e) => { e.stopPropagation(); onToggleMute(); }}
          className="w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center"
          data-testid="hero-mute-btn"
        >
          {muted ? <VolumeX className="w-4 h-4 text-white" /> : <Volume2 className="w-4 h-4 text-white" />}
        </button>
      </div>
      {/* Bottom overlay — strong gradient to prevent text overlap */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-3">
        <h3 className="text-base font-bold text-white leading-tight">{video.title}</h3>
        <button
          onClick={(e) => { e.stopPropagation(); onArtistClick(video); }}
          className="text-xs text-gray-300 hover:text-white mt-0.5"
        >
          {video.artist}
        </button>
        <div className="flex items-center gap-1.5 mt-1.5 text-white/70">
          <Play className="w-3 h-3" />
          <span className="text-[11px] font-medium">Tap to Watch</span>
        </div>
      </div>
    </div>
  );
};

// ─── Trending Video Card / Latest Upload Card ────────────────
// (moved to /components/RailCards.js so the /discover/trending and
//  /discover/latest destination grids can reuse the EXACT same components.
//  Home rail rendering is unchanged.)

// ─── Main Home Page ──────────────────────────────────────────
export const HomePage = () => {
  const [videos, setVideos] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [heroMuted, setHeroMuted] = useState(true);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [commentVideoId, setCommentVideoId] = useState(null);
  const [playlistVideoId, setPlaylistVideoId] = useState(null);
  const { user, axiosInstance } = useAuth();
  const { promptAuth } = useAuthPrompt();
  const navigate = useNavigate();
  const { playVideo } = usePlayer();


  // Marquee feed (Featured Quality Gate): only admin-approved videos rotate
  // through the hero. Backend backfills with most-played approved videos when
  // the featured pool is small, so the surface is never empty.
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const res = await axiosInstance.get('/featured?limit=12');
        setFeaturedVideos(res.data?.videos || []);
      } catch {
        setFeaturedVideos([]);
      }
    };
    fetchFeatured();
  }, [axiosInstance]);

  const fetchVideos = useCallback(async () => {
    try {
      // 50 is enough to feed Trending (top 10) + Latest Uploads (top 20).
      const params = { limit: 50 };
      if (selectedGenre) params.genre = selectedGenre;
      if (searchQuery) params.search = searchQuery;
      const res = await axiosInstance.get('/videos', { params });
      setVideos(res.data || []);
    } catch (err) {
      console.error('Failed to fetch videos:', err);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, searchQuery, selectedGenre]);

  useEffect(() => { fetchVideos(); }, [selectedGenre, searchQuery, fetchVideos]);

  const getThumbnail = (video) => {
    // Single source of truth: priority custom > Bunny > legacy → AUDIVUE
    // branded placeholder (no caller-supplied fallback so stock photos can't
    // override the creator's pick if they leave thumbnail blank).
    return resolveThumbnailUrl(video);
  };

  const handleLikeVideo = async (videoId, isLiked) => {
    if (!user) { promptAuth('Sign up to like videos and save your favorites.'); return; }
    try {
      if (isLiked) {
        await axiosInstance.post(`/favorites/${videoId}`);
      } else {
        await axiosInstance.delete(`/favorites/${videoId}`);
      }
    } catch (err) {
      console.error('Like toggle failed:', err);
    }
  };

  const handlePlaylistAction = (video) => {
    if (!user) { promptAuth('Sign up to create playlists and save videos.'); return; }
    setPlaylistVideoId(video.id);
  };

  const handlePlayVideo = (video, allVideos, index) => {
    playVideo(video, allVideos, index, true);
  };

  const goToArtist = (video) => {
    const artistId = video.user_id || video.artist_id || encodeURIComponent(video.artist || 'unknown');
    navigate(`/creator/${artistId}`);
  };

  // Sections
  // Lyric videos are demoted everywhere on the home page — they don't appear
  // in trending or as the hero. (Backend also pushes them to bottom of /videos.)
  const isLyric = (v) => Boolean(v?.is_lyric) || (v?.style || '').toLowerCase() === 'lyrics';
  const nonLyricVideos = useMemo(() => videos.filter((v) => !isLyric(v)), [videos]);

  // Top performers by score — wider pool (25) so smaller creators get hero
  // placement too, not just the same 5–10 winners.
  const topByScore = useMemo(() =>
    [...nonLyricVideos]
      .sort((a, b) => ((b.views || 0) + (b.likes || 0)) - ((a.views || 0) + (a.likes || 0)))
      .slice(0, 25),
    [nonLyricVideos]
  );

  // Trending Now: randomized sample from the top 40 by score. Auto-
  // rotates every 20 s while the tab is visible (iter 124) so the row
  // feels alive and cycles more of the catalog past a viewer sitting on
  // Home. Wider pool (40 → 10) means someone who sits on the page for
  // a couple of minutes sees 20-30 different trending videos rather than
  // the same 10 all day.
  //
  // `rotationTick` bumps every 20 s → the useMemo below recomputes and
  // returns a fresh shuffle. Ticker uses `document.visibilityState` so
  // we don't burn CPU on a hidden tab.
  const [rotationTick, setRotationTick] = useState(0);
  useEffect(() => {
    let interval = null;
    const start = () => {
      if (interval) return;
      interval = setInterval(() => setRotationTick((t) => t + 1), 20_000);
    };
    const stop = () => {
      if (interval) { clearInterval(interval); interval = null; }
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') start(); else stop();
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const trending = useMemo(() => {
    const pool = [...nonLyricVideos]
      .sort((a, b) => ((b.views || 0) + (b.likes || 0)) - ((a.views || 0) + (a.likes || 0)))
      .slice(0, 40);
    // Fisher-Yates on the pool. rotationTick is in deps so a new tick
    // produces a fresh order; nonLyricVideos in deps so late-arriving
    // fetches also trigger a re-shuffle without waiting for the timer.
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    return pool.slice(0, 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rotationTick intentionally forces a reshuffle
  }, [nonLyricVideos, rotationTick]);

  const latestUploads = useMemo(() =>
    [...nonLyricVideos].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 20),
    [nonLyricVideos]
  );

  // Rotate hero every hour through the FEATURED pool (Featured Quality Gate).
  // Falls back to top-by-score if /api/featured hasn't responded yet.
  // Stable within a session (no flicker on refresh), but visiting the home page
  // later in the day surfaces a different featured video.
  const hourSinceEpoch = Math.floor(Date.now() / 3_600_000);
  const heroPool = featuredVideos.length > 0 ? featuredVideos.filter((v) => !isLyric(v)) : topByScore;
  const heroIndex = heroPool.length > 0 ? hourSinceEpoch % heroPool.length : 0;
  const heroVideo = heroPool[heroIndex];
  // Exclude the hero from Trending Now so it doesn't appear twice on the page
  const trendingList = trending.filter((v) => v?.id !== heroVideo?.id);
  const isSearching = searchQuery || selectedGenre;

  return (
    <div className="bg-black min-h-screen pb-24 md:pb-12">

      {/* ─── Brand Header (wordmark-only on homepage, per spec) ─── */}
      <div
        className="px-4 pb-1"
        style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
      >
        <Link
          to="/"
          className="inline-block"
          aria-label="AUDIVUE home"
          data-testid="home-header-wordmark-link"
        >
          <img
            src="/wordmark-tight.png"
            alt="AUDIVUE"
            className="h-7 md:h-8 w-auto object-contain select-none"
            data-testid="home-header-wordmark"
          />
        </Link>
      </div>

      {/* ─── Hero Video ─── */}
      {!isSearching && heroVideo && (
        <div className="px-4 mt-3">
          <HeroCard
            video={heroVideo}
            thumbnail={getThumbnail(heroVideo, 0)}
            onClick={() => handlePlayVideo(heroVideo, trending, 0)}
            onArtistClick={goToArtist}
            muted={heroMuted}
            onToggleMute={() => setHeroMuted(!heroMuted)}
          />
        </div>
      )}

      {/* ─── 24/7 Live Channel banner (iter 116) ───
          Placed AT THE TOP of Home per spec (below the spotlight hero,
          above the search bar). Renders nothing when the channel is empty
          — safe to leave permanently mounted. Tap navigates to /live. */}
      {!isSearching && (
        <div className="px-4 mt-3">
          <LiveBanner />
        </div>
      )}

      {/* ─── Search Bar ─── */}
      <div className="px-4 mt-5">
        <div className="relative flex items-center">
          <Search className="absolute left-4 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search videos, artists, songs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-gray-900 rounded-full pl-11 pr-12 py-3 text-sm text-white placeholder:text-gray-500 outline-none focus:ring-2 focus:ring-purple-500/30"
            data-testid="search-input"
          />
          <button className="absolute right-3 w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
            <SlidersHorizontal className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      {/* ─── Music | Film toggle ─── */}
      <div className="flex items-center gap-2 px-4 mt-4" data-testid="mode-toggle">
        <span className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-white text-black" data-testid="toggle-music-active">
          <MusicIcon className="w-4 h-4" /> Music
        </span>
        <button onClick={() => navigate('/films')} data-testid="toggle-film" className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors">
          <FilmIcon className="w-4 h-4" /> Film
        </button>
      </div>

      {/* ─── Category Pills ─── */}
      <div className="flex gap-2 px-4 mt-4 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
        {GENRE_PILLS.map((g) => {
          const isActive = (g === 'All' && !selectedGenre) || selectedGenre === g;
          return (
            <button
              key={g}
              onClick={() => setSelectedGenre(g === 'All' ? '' : g)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                isActive
                  ? 'bg-white text-black'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
              data-testid={`filter-${g.toLowerCase()}`}
            >
              {g}
            </button>
          );
        })}
      </div>

      {/* ─── Trending Now ─── */}
      {!isSearching && trendingList.length > 0 && (
        <div className="mt-6" data-testid="trending-section">
          <div className="px-4 mb-3 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white flex items-center gap-2" data-testid="trending-heading"><Flame className="w-5 h-5 text-orange-400" />Trending Now</h2>
            <button onClick={() => navigate('/discover/trending')} className="text-sm text-orange-400 font-medium flex items-center gap-0.5" data-testid="trending-view-all">
              View All <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-3 overflow-x-auto px-4 pb-2" style={{ scrollbarWidth: 'none' }}>
            {trendingList.map((video, idx) => (
              <TrendingCard
                key={video.id}
                video={video}
                thumbnail={getThumbnail(video, idx + 1)}
                onClick={() => handlePlayVideo(video, trendingList, idx)}
                onArtistClick={goToArtist}
                onLike={handleLikeVideo}
                onPlaylist={(video) => handlePlaylistAction(video)}
                onShare={(video) => shareVideo(video)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ─── Latest Uploads ─── */}
      {!isSearching && latestUploads.length > 0 && (
        <div className="mt-6" data-testid="latest-section">
          <div className="px-4 mb-3 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white" data-testid="latest-heading">Latest Uploads</h2>
            <button onClick={() => navigate('/discover/latest')} className="text-sm text-orange-400 font-medium flex items-center gap-0.5" data-testid="latest-view-all">
              View All <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-3 overflow-x-auto px-4 pb-2" style={{ scrollbarWidth: 'none' }}>
            {latestUploads.map((video, idx) => (
              <LatestCard
                key={video.id}
                video={video}
                thumbnail={getThumbnail(video, idx)}
                onClick={() => handlePlayVideo(video, latestUploads, idx)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ─── Search Results ─── */}
      {isSearching && (
        <div className="px-4 mt-4">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[...Array(6)].map((_, i) => (
                <div key={`skeleton-${i}`}>
                  <div className="aspect-[3/4] bg-gray-800 rounded-xl animate-pulse mb-2" />
                  <div className="h-3 bg-gray-800 rounded animate-pulse w-3/4 mb-1" />
                  <div className="h-3 bg-gray-800 rounded animate-pulse w-1/2" />
                </div>
              ))}
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-sm">No videos found</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {videos.map((video, idx) => (
                <TrendingCard
                  key={video.id}
                  video={video}
                  thumbnail={getThumbnail(video, idx)}
                  onClick={() => handlePlayVideo(video, videos, idx)}
                  onArtistClick={goToArtist}
                  onLike={handleLikeVideo}
                  onPlaylist={(video) => handlePlaylistAction(video)}
                  onShare={(video) => shareVideo(video)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <CommentModal
        videoId={commentVideoId}
        isOpen={!!commentVideoId}
        onClose={() => setCommentVideoId(null)}
      />
      <AddToPlaylistModal
        videoId={playlistVideoId}
        isOpen={!!playlistVideoId}
        onClose={() => setPlaylistVideoId(null)}
      />
    </div>
  );
};
